import { SubscriptionFeatureFlag } from '@mm/state/controllers/FeatureFlags';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { CompanyLevelFeatureFlag } from '@mm/state/model/CompanyModel';
import {
  getPlanFromBillingType,
  nextBillingTypeForFeatureSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { PlanBadge } from '../../../../common/components/ProBadge';
import SendLimitAlert from '../../SidebarHeader/SendLimitAlert/SendLimitAlert';
import { FollowConvosItem } from '../../ActivePageSlideMenu/ActivePageSlideMenu';
import './TopLevelSidebarLink.pcss';
import { FollowUpChats } from './FollowUpChats';
import { RightOutlined } from '@ant-design/icons';

type Props = {
  link?: string;
  onClick?: (e?: MouseEvent<HTMLDivElement>) => any;
  text?: string;
  isActive?: boolean;
  className?: string;
  followUpCount?: Record<number, FollowConvosItem | undefined>;
  icon?: string | null;
  iconComponent?: React.ReactFragment;
  activeFacebookPage?: EnhancedBillingPage;
  requiredFeature?: SubscriptionFeatureFlag | CompanyLevelFeatureFlag;
  withRightArrow?: boolean;
};

const TopLevelSidebarLink = (props: Props) => {
  const {
    onClick,
    text,
    isActive,
    className,
    link,
    followUpCount,
    iconComponent,
    activeFacebookPage,
    requiredFeature,
    withRightArrow,
  } = props;
  const hasTwilioNumber = useSelector(hasSMSNumberSelector);

  const planInfo = getPlanInfo(activeFacebookPage);
  const showSendLimitAlert =
    (planInfo.isCustomTeam || planInfo.isLegacyTeamPlan) && hasTwilioNumber;

  const count =
    activeFacebookPage && followUpCount
      ? followUpCount[activeFacebookPage?.remoteId]
      : null;

  const billingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, requiredFeature),
  );

  const isUnicornBackgroundColor =
    getPlanFromBillingType(billingType) === 'unicorn';

  const titleWrapperClassList = classnames(
    'top-level-sidebar-link',
    className,
    {
      'top-level-sidebar-link--active-group-link': isActive,
      'top-level-sidebar-link--unicorn-color': isUnicornBackgroundColor,
      'top-level-sidebar-link--with-plan-badge': !!billingType,
    },
  );

  if (link) {
    return (
      <a
        href={link}
        className={titleWrapperClassList}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className="top-level-sidebar-link__title-text">{text}</div>
      </a>
    );
  }

  return (
    <div className={titleWrapperClassList} onClick={onClick}>
      {billingType && (
        <PlanBadge
          className="top-level-sidebar-link__plan-icon"
          billingType={billingType}
          size={10}
        />
      )}
      <div className="top-level-sidebar-link__link-container">
        {iconComponent ? iconComponent : null}
        <div className="top-level-sidebar-link__title-text">{text}</div>
        {count ? <FollowUpChats count={count} showFullValue={false} /> : null}
        {showSendLimitAlert && text === 'SMS' && <SendLimitAlert />}
      </div>
      {withRightArrow && (
        <RightOutlined className="sidebar-menu-group__right-arrow" />
      )}
    </div>
  );
};

export default TopLevelSidebarLink;
